import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  alpha,
  useTheme
} from '@mui/material';
import {
  Group as GroupIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  ManageAccounts as ManageMembersIcon,
  Event as EventIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useGroups } from '../contexts/GroupContext';
import GroupForm from './GroupForm';
import GroupMemberManagement from './GroupMemberManagement';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function GroupCard({ group }) {
  const { deleteGroup, isUserCreator, isUserAdmin, isUserMember, addMemberToGroup, leaveGroup } = useGroups();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [memberManagementOpen, setMemberManagementOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [joining, setJoining] = useState(false);
  const [leaveError, setLeaveError] = useState('');

  const canEdit = isUserCreator(group.id);
  const canDelete = isUserCreator(group.id);
  const canManageMembers = isUserAdmin(group.id) || isUserCreator(group.id);
  const isMember = isUserMember(group.id);
  const canJoin = currentUser && !isMember && !isUserCreator(group.id);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setEditDialogOpen(true);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleManageMembers = () => {
    handleMenuClose();
    setMemberManagementOpen(true);
  };

  const handleJoinGroup = async () => {
    if (!currentUser) return;
    setJoining(true);
    try {
      await addMemberToGroup(group.id, currentUser.email, 'member');
    } catch (error) {
      console.error('Error joining group:', error);
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!currentUser || !isMember) return;
    if (!window.confirm(`Are you sure you want to leave "${group.name}"?`)) return;

    setLeaveError('');
    try {
      await leaveGroup(group.id);
    } catch (error) {
      console.error('Error leaving group:', error);
      setLeaveError(error.message);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteGroup(group.id);
      setDeleteDialogOpen(false);
    } catch (error) {
      setDeleteError(error.message || 'Failed to delete group');
    } finally {
      setDeleting(false);
    }
  };

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case 'private': return '🔒';
      case 'friends': return '👥';
      case 'public': return '🌍';
      default: return '🔒';
    }
  };

  const getPrivacyLabel = (privacy) => {
    switch (privacy) {
      case 'private': return 'Private';
      case 'friends': return 'Friends Only';
      case 'public': return 'Public';
      default: return 'Private';
    }
  };

  const getPrivacyColor = (privacy) => {
    switch (privacy) {
      case 'private': return 'default';
      case 'friends': return 'primary';
      case 'public': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString();
    } catch (error) {
      return 'Recently';
    }
  };

  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'visible',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            borderRadius: '4px 4px 0 0'
          }
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          {/* Header with name and menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: '10px',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  display: 'flex'
                }}
              >
                <GroupIcon />
              </Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                {group.name}
              </Typography>
            </Box>

            {(canEdit || canDelete) && (
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                aria-label="group actions"
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>

          {/* Description */}
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 3, minHeight: 40 }}>
            {group.description || 'No description provided'}
          </Typography>

          {/* Leave Group Error Display */}
          {leaveError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLeaveError('')}>
              {leaveError}
            </Alert>
          )}

          {/* Group stats */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha('#fff', 0.05)}`
              }}
            >
              <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                {group.memberCount || 1} member{(group.memberCount || 1) !== 1 ? 's' : ''}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha('#fff', 0.05)}`
              }}
            >
              <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                {formatDate(group.createdAt)}
              </Typography>
            </Box>
          </Box>

          {/* Privacy and status */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Chip
              icon={<span style={{ fontSize: '1rem' }}>{getPrivacyIcon(group.privacy)}</span>}
              label={getPrivacyLabel(group.privacy)}
              size="small"
              color={getPrivacyColor(group.privacy)}
              variant="outlined"
              sx={{ backdropFilter: 'blur(4px)' }}
            />

            {group.isActive ? (
              <Chip
                label="Active"
                size="small"
                color="success"
                variant="outlined"
                sx={{
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  borderColor: alpha(theme.palette.success.main, 0.2)
                }}
              />
            ) : (
              <Chip
                label="Inactive"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>

        <CardActions sx={{ p: 2, pt: 0 }}>
          {canJoin ? (
            <Button
              size="small"
              color="primary"
              variant="contained"
              fullWidth
              onClick={handleJoinGroup}
              disabled={joining}
              sx={{ borderRadius: 2 }}
            >
              {joining ? 'Joining...' : 'Join Group'}
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <Button
                size="small"
                color="primary"
                variant="contained"
                fullWidth
                startIcon={<EventIcon />}
                onClick={() => navigate('/parties')}
                sx={{ borderRadius: 2 }}
              >
                Plan Party
              </Button>

              <Button
                size="small"
                color="inherit"
                variant="outlined"
                fullWidth
                startIcon={<ViewIcon />}
                onClick={() => navigate('/calendar', { state: { activeTab: 1, selectedGroup: group.id } })}
                sx={{ borderRadius: 2, borderColor: alpha('#fff', 0.2) }}
              >
                Availability
              </Button>
            </Box>
          )}
        </CardActions>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 180,
            mt: 1
          }
        }}
      >
        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit Group" />
          </MenuItem>
        )}

        <MenuItem onClick={() => navigate('/calendar', { state: { activeTab: 1, selectedGroup: group.id } })}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View Availability" />
        </MenuItem>

        {canManageMembers && (
          <MenuItem onClick={handleManageMembers}>
            <ListItemIcon>
              <ManageMembersIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Manage Members" />
          </MenuItem>
        )}

        {isMember && !isUserCreator(group.id) && (
          <MenuItem onClick={handleLeaveGroup} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Leave Group" />
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Delete Group" />
          </MenuItem>
        )}
      </Menu>

      {/* Dialogs */}
      <GroupForm
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        group={group}
        mode="edit"
      />

      <GroupMemberManagement
        open={memberManagementOpen}
        onClose={() => setMemberManagementOpen(false)}
        group={group}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha('#fff', 0.1)}`
          }
        }}
      >
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText color="text.secondary">
            Are you sure you want to delete <strong>"{group.name}"</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleting}
            variant="contained"
            color="error"
          >
            {deleting ? 'Deleting...' : 'Delete Group'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
